import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, prefersReducedMotion, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * Spring constants for the card hover image swap — flickity's default
 * `selectedAttraction`/`friction`, integrated at a fixed 60fps timestep with
 * render interpolation so the motion is identical and smooth on every
 * display. SWAP_TIME_SCALE calibrates to the curve measured live on
 * theheightsdistrict.com cards (foreground tab, rAF-sampled: 50% ≈ 184ms,
 * 75% ≈ 267ms, 90% ≈ 368ms, settle ≈ 600ms) — their page's mild jank runs
 * the spring at ~0.95× flickity's canonical pace.
 */
const SWAP_ATTRACTION = 0.025;
const SWAP_FRICTION = 0.28;
const SWAP_STEP_MS = 1000 / 60;
const SWAP_TIME_SCALE = 0.95;

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const variantOverride = this.#getProductPageVariantOverride();

    if (variantOverride?.action === 'set') {
      url.searchParams.set('variant', variantOverride.variantId);
    } else if (variantOverride?.action === 'remove') {
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  /**
   * Gets the user-selected variant ID that still needs to be synced from product-card state.
   * @returns {string | null} Variant ID or null.
   */
  getSelectedVariantId() {
    const variantOverride = this.#getProductPageVariantOverride();
    return variantOverride?.action === 'set' ? variantOverride.variantId : null;
  }

  /**
   * Gets the user-selected variant override for product-page URLs.
   * @returns {{ action: 'set', variantId: string } | { action: 'remove' } | null}
   */
  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;
    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#getCheckedVariantId();
    if (variantId === undefined) return null;

    return variantId ? { action: 'set', variantId } : { action: 'remove' };
  }

  /** @returns {string | undefined} */
  #getCheckedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
      this.#renderDots();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopSwapAnimation();
    this.#unbindScrub();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId, variantId, connectedProductUrl } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    // Empty string removes ?variant=.
    if (typeof variantId === 'string') {
      this.applyVariantToLinks(variantId, typeof connectedProductUrl === 'string' ? connectedProductUrl : undefined);
    }

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html, typeof variantId === 'string' ? variantId : undefined);
        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        this.#updateVariantImages();
        this.#previousSlideIndex = null;
        this.#renderDots();

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   * @param {string} [intendedVariantId] - User-intended variant.
   */
  #updateProductUrl(html, intendedVariantId) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const responseUrl = new URL(anchorElement.href);
      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId: intendedVariantId ?? responseUrl.searchParams.get('variant'),
      });
    }
  }

  /**
   * Applies the given variant ID to the variant param of every link in the card.
   * Public so the components, namely swatches component can call it.
   * @param {string | null} variantId - The variant ID to set, or null to remove it.
   * @param {string} [productUrl] - Product URL path to adopt.
   */
  applyVariantToLinks(variantId, productUrl) {
    let pathname;
    if (productUrl) {
      try {
        pathname = new URL(productUrl, window.location.origin).pathname;
      } catch (error) {
        console.warn('[product-card] Invalid product URL:', productUrl, error);
        return;
      }
    }

    this.#updateLinks({ pathname, variantId });
  }

  /**
   * Updates every link in the card (overlay, gallery, and title) in place, preserving
   * each link's existing query params so tracking params stay in sync.
   * Always sets (or removes) the `variant` param; optionally adopts a new pathname (for combined-listing child navigation).
   * @param {object} options
   * @param {string} [options.pathname] - New pathname to adopt (combined-listing child navigation). Omit to keep each link's current path.
   * @param {string | null} [options.variantId] - Variant ID to set, or null/undefined to remove it.
   */
  #updateLinks({ pathname, variantId }) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    for (const linkEl of [productCardLink, cardGalleryLink, productTitleLink]) {
      if (!(linkEl instanceof HTMLAnchorElement) || !linkEl.href) continue;
      const url = new URL(linkEl.href);
      if (pathname) url.pathname = pathname;
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      linkEl.href = url.toString();
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    // Scroll events emitted by the hover-swap spring report `userInitiated`,
    // but they must not overwrite the slide the user deliberately chose.
    if (this.#swapAnimation) return;

    this.#setActiveDot(event.detail.index);
    this.#syncSlideVideo(event.detail.index);

    // The spring's settle write (scrollLeft = target) emits ONE async scroll
    // event which the slideshow reports as `userInitiated` with trigger
    // 'scroll'. Recording it would pin every later hover preview to that
    // slide (the video kept playing across zones). Only genuine gestures
    // ('drag') and explicit selects may set the restore slide.
    if (event.detail.userInitiated && event.detail.trigger !== 'scroll') {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Renders the Heights-style page dots as a sibling of the card gallery
   * (outside its overflow clip), one dot per visible slide. CSS hides the
   * first dot and only shows the pill on hover-capable desktop viewports,
   * so cards with 2 images end up dotless — exactly like Heights'
   * `without-dots` carousels. Re-run after variant updates change the
   * visible slide set.
   */
  #renderDots() {
    const { slideshow, cardGallery } = this.refs;
    const slideCount = Math.min(slideshow?.slides?.length ?? 0, 4);

    let anchor = cardGallery?.nextElementSibling;
    if (!(anchor instanceof HTMLElement) || !anchor.classList.contains('card-gallery__dots-anchor')) {
      anchor = null;
    }

    if (!cardGallery || slideCount < 3) {
      anchor?.remove();
      return;
    }

    if (!anchor) {
      anchor = document.createElement('div');
      anchor.className = 'card-gallery__dots-anchor';
      anchor.setAttribute('aria-hidden', 'true');
      anchor.innerHTML = '<div class="card-gallery__dots"></div>';
      cardGallery.after(anchor);
    }

    // The anchor is a zero-height flex item, so the parent's row-gap would
    // both push the card content down and offset the dots. Pull the anchor up
    // by one gap to cancel both.
    const rowGap = Number.parseFloat(getComputedStyle(cardGallery.parentElement ?? anchor).rowGap) || 0;
    anchor.style.marginBlockStart = rowGap ? `${-rowGap}px` : '';

    const dots = anchor.firstElementChild;
    if (!dots) return;

    while (dots.children.length > slideCount) dots.lastElementChild?.remove();
    while (dots.children.length < slideCount) dots.appendChild(document.createElement('span')).className = 'card-gallery__dot';

    this.#setActiveDot(this.refs.slideshow?.current ?? 0);
  }

  /**
   * Marks the dot for the given slide index as selected.
   * @param {number} index - The slide index.
   */
  #setActiveDot(index) {
    const anchor = this.refs.cardGallery?.nextElementSibling;
    if (!(anchor instanceof HTMLElement) || !anchor.classList.contains('card-gallery__dots-anchor')) return;

    const dots = anchor.firstElementChild?.children;
    if (!dots) return;

    for (let i = 0; i < dots.length; i++) {
      dots[i]?.toggleAttribute('data-selected', i === index);
    }
  }

  /**
   * Whether this card's videos have started buffering (done once, on the
   * first hover of the card).
   */
  #videosWarmed = false;

  /**
   * Starts buffering every card video the moment the pointer enters the
   * gallery. Videos ship with preload="none" (zero cost at page load); the
   * lazy fetch + decoder init used to kick in only when the scrub reached
   * the clip's zone, hitching the spring mid-flight. Once a video has data,
   * its playback pipeline is primed off-screen (muted play, paused after the
   * first presented frame): the FIRST play() of a video starts the decoder
   * clock and can hitch the spring even when fully buffered.
   */
  #warmSlideVideos() {
    if (this.#videosWarmed) return;
    this.#videosWarmed = true;

    for (const slide of this.refs.slideshow?.slides ?? []) {
      const video = slide.querySelector('video.card-gallery__video');
      if (!(video instanceof HTMLVideoElement) || video.preload === 'auto') continue;

      video.preload = 'auto';
      video.load();

      video.addEventListener(
        'loadeddata',
        () => {
          // The scrub may already want this video playing — leave it alone.
          if (this.#activeVideo === video || !video.paused) return;

          video
            .play()
            .then(() => {
              const rest = () => {
                if (this.#activeVideo === video) return;
                video.pause();
                video.currentTime = 0;
              };
              // Pause only after a frame actually presented, so the decoder
              // is genuinely hot; fall back to an immediate pause.
              if (typeof video.requestVideoFrameCallback === 'function') {
                video.requestVideoFrameCallback(rest);
              } else {
                rest();
              }
            })
            .catch(() => {});
        },
        { once: true },
      );
    }
  }

  /**
   * The card video that is currently meant to be playing, if any.
   * @type {HTMLVideoElement | null}
   */
  #activeVideo = null;

  /**
   * Adjusts card videos for a spring launch/retarget. Target slide has a
   * clip → start it. Target has none → FREEZE the outgoing clip in place
   * (`pause()` only — visually seamless, the current frame just stops
   * advancing). Measured with requestVideoFrameCallback: a clip left
   * playing through the exit spring stalls ~180ms when the neighbouring
   * card's video warms its decoder at the same instant — the visible
   * "blink" on leaving the clip zone. A frozen frame cannot stall. The
   * rewind to frame 0 still waits for `#syncSlideVideo` at settle, once
   * the clip is off-screen.
   * @param {number} index - The slide index the spring is heading to.
   */
  #cueSlideVideo(index) {
    const video = this.refs.slideshow?.slides?.[index]?.querySelector('video.card-gallery__video');

    if (video instanceof HTMLVideoElement) {
      this.#activeVideo = video;
      if (video.paused) video.play().catch(() => {});
    } else if (this.#activeVideo && !this.#activeVideo.paused) {
      this.#activeVideo.pause();
    }
  }

  /**
   * Plays the target slide's card video and stops every other one, rewinding
   * it to its first frame — scrubbing back to a clip always restarts it from
   * the beginning. Runs at rest (spring settle / slideshow select), never
   * mid-flight, so the pause+rewind is invisible. `play()` is guarded so
   * repeated syncs never spam the engine with redundant play calls.
   * @param {number} index - The slide index in view.
   */
  #syncSlideVideo(index) {
    const slides = this.refs.slideshow?.slides;
    if (!slides) return;

    /** @type {HTMLVideoElement | null} */
    let active = null;

    slides.forEach((slide, i) => {
      const video = slide.querySelector('video.card-gallery__video');
      if (!(video instanceof HTMLVideoElement)) return;

      if (i === index) {
        active = video;
        if (video.paused) video.play().catch(() => {});
      } else {
        if (!video.paused) video.pause();
        if (video.currentTime !== 0) video.currentTime = 0;
      }
    });

    this.#activeVideo = active;
  }

  /**
   * State of the in-flight hover-swap spring animation. `snap` holds the
   * scroller's inline scroll-snap-type for restoration at settle.
   * @type {{ raf: number, base: number, x: number, prevX: number, velocity: number, target: number, index: number, last: number, acc: number, slides: HTMLElement[], snap: string } | null}
   */
  #swapAnimation = null;

  /**
   * Slides the card gallery to a slide with the Heights flickity spring
   * (attraction/friction attractor), integrated at a fixed 60fps timestep so
   * the motion is identical on every display. The scroller is FROZEN during
   * the spring and the strip is rendered with per-slide translate3d instead
   * of scrollLeft: scroll positions quantize to whole pixels, which judders
   * on the slow spring tail, while transforms render sub-pixel on the GPU —
   * exactly how flickity positions Heights' cards. Retargeting mid-flight
   * keeps the current velocity, so rapid hover in/out reverses smoothly.
   * @param {number} index - The target slide index (in `slideshow.slides` space).
   * @returns {boolean} Whether the slide was animated (or instantly selected under reduced motion).
   */
  #animateSwapToSlide(index) {
    const { slideshow } = this.refs;
    const scroller = slideshow?.refs.scroller;
    const slide = slideshow?.slides?.[index];

    if (!slideshow || !(scroller instanceof HTMLElement) || !slide) return false;

    // Load the target image before the spring starts revealing it.
    slide.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
    this.#setActiveDot(index);
    // Only START the target's clip here; pausing/rewinding the outgoing one
    // waits for settle so the visible video never snaps to frame 0 mid-swap.
    this.#cueSlideVideo(index);

    if (prefersReducedMotion()) {
      slideshow.select(index, undefined, { animate: false });
      return true;
    }

    const target = slide.offsetLeft;

    if (this.#swapAnimation) {
      this.#swapAnimation.target = target;
      this.#swapAnimation.index = index;
      return true;
    }

    // Settled at the target already (common under pointermove): nothing to do.
    if (Math.abs(scroller.scrollLeft - target) < 1) return true;

    const slides = slideshow.slides ?? [];

    this.#swapAnimation = {
      raf: 0,
      base: scroller.scrollLeft,
      x: scroller.scrollLeft,
      prevX: scroller.scrollLeft,
      velocity: 0,
      target,
      index,
      last: performance.now(),
      acc: 0,
      slides,
      snap: scroller.style.scrollSnapType,
    };

    // Chrome's scroll snap tracks the TRANSFORMED snap areas: while the
    // spring translates the slides, any unrelated layout change on the page
    // (hovering the next card mutates its images/dots) makes the engine
    // re-snap scrollLeft onto the moving snap area — mirroring the spring
    // 1:1, visually freezing the strip, then jumping wholesale at settle
    // (the exit "blink"). Snap is disabled while the spring owns the strip
    // and restored right after the settle landing write.
    scroller.style.scrollSnapType = 'none';

    const tick = (/** @type {number} */ now) => {
      const anim = this.#swapAnimation;
      if (!anim) return;

      // Fixed-timestep accumulator; the clamp avoids a burst of catch-up
      // steps after a background-tab stall.
      anim.acc += Math.min(now - anim.last, 100) * SWAP_TIME_SCALE;
      anim.last = now;

      while (anim.acc >= SWAP_STEP_MS) {
        anim.acc -= SWAP_STEP_MS;
        anim.prevX = anim.x;
        // Flickity's integration order: accelerate, move, then dampen.
        anim.velocity += (anim.target - anim.x) * SWAP_ATTRACTION;
        anim.x += anim.velocity;
        anim.velocity *= 1 - SWAP_FRICTION;
      }

      if (Math.abs(anim.target - anim.x) < 0.5 && Math.abs(anim.velocity) < 0.5) {
        const settledIndex = anim.index;
        const settledTarget = anim.target;
        // Land the real scroll position, clear transforms, and restore snap
        // in the SAME frame — one atomic, visually no-op commit. The
        // scroller has `scroll-behavior: smooth`; force the landing write
        // instant or it would visibly re-animate.
        const previousBehavior = scroller.style.scrollBehavior;
        scroller.style.scrollBehavior = 'auto';
        scroller.scrollLeft = settledTarget;
        scroller.style.scrollBehavior = previousBehavior;
        this.#stopSwapAnimation();
        // Position already matches the target, so this only syncs
        // aria-hidden/current bookkeeping when the scroll handler hasn't.
        slideshow.select(settledIndex, undefined, { animate: false });
        // At rest the outgoing clip is off-screen: now pause + rewind it.
        this.#syncSlideVideo(settledIndex);
        return;
      }

      // The spring owns the scroll position for the whole flight: pin it
      // against any native drift (snap chase, anchoring, stray wheel).
      if (scroller.scrollLeft !== anim.base) {
        const behavior = scroller.style.scrollBehavior;
        scroller.style.scrollBehavior = 'auto';
        scroller.scrollLeft = anim.base;
        scroller.style.scrollBehavior = behavior;
      }

      // Interpolate between the previous and current physics states so the
      // strip advances every display frame, sub-pixel, on the compositor.
      const alpha = Math.min(anim.acc / SWAP_STEP_MS, 1);
      const offset = anim.prevX + (anim.x - anim.prevX) * alpha - anim.base;
      for (const el of anim.slides) {
        el.style.transform = `translate3d(${-offset}px, 0, 0)`;
      }
      anim.raf = requestAnimationFrame(tick);
    };

    this.#swapAnimation.raf = requestAnimationFrame(tick);
    return true;
  }

  /**
   * Cancels the hover-swap animation and parks the per-slide transforms at
   * IDENTITY instead of removing them. Dropping the transform demotes the
   * slide's compositor layer and the next spring start re-promotes it;
   * either event re-parents a playing clip's video layer, which flashes one
   * frame while the clip is on screen (the blink on leaving the clip zone —
   * invisible on image slides, which is why arrival always looked fine).
   * `translate3d(0, 0, 0)` is visually a no-op but keeps the layer alive.
   */
  #stopSwapAnimation() {
    const anim = this.#swapAnimation;
    if (!anim) return;

    cancelAnimationFrame(anim.raf);
    for (const el of anim.slides) {
      el.style.transform = 'translate3d(0, 0, 0)';
    }
    this.refs.slideshow?.refs.scroller?.style.setProperty('scroll-snap-type', anim.snap || '');
    this.#swapAnimation = null;
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Picks the hover target slide from the pointer's X position, mirroring
   * Heights' scrub zones: 2 media → always the second image; 3 media → left
   * and right halves; 4 media → thirds. Slide 0 is never a hover target.
   * @param {PointerEvent} event - The pointer event.
   * @param {number} count - The number of visible slides (capped at 4).
   * @returns {number} The target slide index (>= 1).
   */
  #hoverZoneIndex(event, count) {
    if (count <= 2) return 1;

    const gallery = this.refs.cardGallery;
    const rect = gallery?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 1;

    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 0.999);
    return 1 + Math.floor(ratio * (count - 1));
  }

  /**
   * The framework's `on:` attributes don't cover `pointermove`, so the scrub
   * listener is attached to the gallery only while the pointer is over it
   * (previewImage on pointerenter binds it; resetImage unbinds).
   */
  #scrubBound = false;

  #handleScrub = (/** @type {PointerEvent} */ event) => this.previewImage(event);

  #unbindScrub() {
    if (!this.#scrubBound) return;
    this.refs.cardGallery?.removeEventListener('pointermove', this.#handleScrub);
    this.#scrubBound = false;
  }

  /**
   * Previews the hovered image. Runs on pointerenter and on every pointermove
   * while over the gallery, so moving across the card scrubs between images
   * like Heights.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow, cardGallery } = this.refs;
    const slides = slideshow?.slides;

    if (!slideshow || !slides || slides.length < 2) return;

    if (!this.#scrubBound && cardGallery) {
      cardGallery.addEventListener('pointermove', this.#handleScrub);
      this.#scrubBound = true;
    }

    // Buffer the card's videos as soon as the pointer is over the media, so
    // arriving at the clip's zone plays instantly instead of hitching.
    this.#warmSlideVideos();

    this.resetVariant.cancel();

    // Absolute targets only: `nextIndex`/`previousIndex` read `current`, which
    // is transitional while the spring is in flight and would occasionally
    // resolve out of range — the cause of instant "blink" swaps.
    const targetIndex =
      this.#previousSlideIndex != null && this.#previousSlideIndex > 0
        ? this.#previousSlideIndex
        : this.#hoverZoneIndex(event, Math.min(slides.length, 4));

    if (targetIndex >= slides.length) return;

    if (!this.#animateSwapToSlide(targetIndex)) {
      slideshow.select(targetIndex, undefined, { animate: false });
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    this.#unbindScrub();

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      // Heights leaves to the first cell, always — absolute, never relative.
      if (!this.#animateSwapToSlide(0)) {
        slideshow.select(0, undefined, { animate: false });
      }
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    /**
     * Animates to the slide carrying the given id, when it is visible.
     * @param {string} id - The slide id.
     * @returns {boolean} Whether the spring animation took over.
     */
    const animateToSlideId = (id) => {
      const index = slideshow.slides?.findIndex((slide) => slide.getAttribute('slide-id') === id) ?? -1;
      return index >= 0 && this.#animateSwapToSlide(index);
    };

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        if (!animateToSlideId(id)) slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      if (!animateToSlideId(slideId)) slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - return to the first slide
    if (!this.#animateSwapToSlide(0)) {
      slideshow.select(0, undefined, { animate: false });
    }
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Handles card swatch changes.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const hasAvailableVariant = clickedSwatch.dataset.hasAvailableVariant === 'true';
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Request the first available variant for this swatch.
    if (isSwatchInput && hasAvailableVariant && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      const optionValueId = clickedSwatch.dataset.optionValueId || '';
      const connectedProductUrl = clickedSwatch.dataset.connectedProductUrl || '';
      const requestUrl = this.buildRequestUrl(clickedSwatch, 'product-card', [optionValueId]);

      this.fetchUpdatedSection(requestUrl, {
        detail: { optionValueId, variantId: firstAvailableVariantId, connectedProductUrl },
      });
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
